import { Component, OnInit, Input, AfterViewInit, ChangeDetectorRef, OnDestroy, ChangeDetectionStrategy, Output, EventEmitter } from '@angular/core';
import { AlertsService } from 'src/modules/base/services/alerts.service';
import { LoaderService } from 'src/modules/base/services/loader.service';
import { BaseService } from 'src/modules/base/shared/services/base.service';
import { RolePermissionService } from 'src/modules/base/shared/services/role-permission.service';
import { FormBuilder, FormGroup, Validators} from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { DialogConfig } from '../../../dialog/dialog-config';
import { AIService } from '../../services/ai.service';
import { HttpHeaders } from '@angular/common/http';


@Component({
    selector: 'app-kx-ai-data-analysis',
    templateUrl: './kx-ai-data-analysis.component.html',
    styleUrls: ['./kx-ai-data-analysis.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class KxAIDataAnalysisComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() getData: EventEmitter<any> = new EventEmitter();
  @Input() config:any={};
  outputConfig:any={};
  promptForm: FormGroup;
  isLoading: boolean = false;
  private async_task_id: string = null;

  constructor(
    public rolePermissionService: RolePermissionService,
    public baseService: BaseService,
    public alertService: AlertsService,
    public loader: LoaderService,
    public aiService: AIService,
    public dialogConfig: DialogConfig,
    private sanitizer: DomSanitizer,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    this.promptForm = this.fb.group({
      prompt: ['', [Validators.required, Validators.minLength(10)]]
    });
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  ngOnInit(): void {
    this.outputConfig = {
      loaded: false,
      noData: false,
      summary: null,
      askData:true,
    };
  }

  

  getSummary(): void {
    if (this.isLoading) return;
    this.isLoading = true;
    this.outputConfig = {
      loaded: false,
      noData: false,
      summary: null,
      askData: false,
    };
    this.cdr.detectChanges();
    const gridConfig = this.dialogConfig.data.aiData.gridConfig;
    const compConfig = this.dialogConfig.data.config;
    this.config.llm_name = gridConfig.prompt_config_current.llm_name;
    this.config.model = gridConfig.prompt_config_current.model;
    const is_async = gridConfig.prompt_config_current.is_async || false;

    const datasource: any = this.aiService.getDatasourceByAiConfig(compConfig, gridConfig);
    datasource.headers = { "headers": new HttpHeaders().set('timeout', "120000") };

    // Build filter: context args (final_url / download_id / download_url) — mirrors getDatasourceByAiConfig args
    var filterArgs = "";
    if (compConfig.final_url) {
      filterArgs = "final_url:" + btoa(compConfig.final_url);
    }
    if (this.config.csvAnalyse) {
      const csvArgs = "download_id:" + this.config.csvAnalyse.id + ",download_url:" + btoa(this.config.csvAnalyse.url);
      filterArgs = filterArgs ? filterArgs + "," + csvArgs : csvArgs;
    }

    const promptValue = this.promptForm.get('prompt').value;

    // POST body for AI_FILL — async supported only on this endpoint
    const aiResponse: any = {};
    aiResponse.prompt_code = datasource.prompt_code;
    aiResponse.message = btoa(encodeURIComponent(promptValue).replace(/%([0-9A-F]{2})/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16))));
    aiResponse.filter = filterArgs;
    aiResponse.type = "is_async:" + ((is_async) ? is_async : false);

    this.baseService.action(aiResponse, "AI_FILL", datasource.application, datasource.resource, undefined, datasource.headers).subscribe({
      next: (data) => {
        if (data.errCode === 0 && data.resource?.length > 0) {
          if (is_async) {
            this.async_task_id = data.resource[0].id;
            setTimeout(() => {
              this.aiService.retryCheckAsyncTask(
                this.async_task_id,
                (asyncRes) => {
                  this.isLoading = false;
                  if (asyncRes) {
                    this.processAnalysisResult(asyncRes, gridConfig);
                  } else {
                    this.outputConfig = {
                      loaded: true,
                      noData: true,
                      askData: false,
                      summary: null
                    };
                    this.cdr.detectChanges();
                  }
                }
              );
            }, 10000);
          } else {
            this.isLoading = false;
            this.processAnalysisResult(data.resource[0], gridConfig);
          }
        } else {
          this.isLoading = false;
          this.alertService.error(data.message);
        }
      },
      error: (_error) => {
        this.alertService.error('Failed to generate response');
        this.outputConfig = {
          loaded: true,
          noData: true,
          askData: false,
          summary: null
        };
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private decodeContent(raw: string): string {
    if (!raw) return '';

    // Step 1: try base64 decode; fall back to raw if it throws or produces garbage
    let decoded = raw;
    try {
      const b64 = atob(raw);
      const isPrintable = /^[\x09\x0A\x0D\x20-\uFFFF]*$/.test(b64);
      if (isPrintable) {
        decoded = b64;
      }
    } catch {
      // not base64 — decoded stays as raw
    }

    // Step 2: if the result is a JSON-encoded string (e.g. "\"html\\nhere\""),
    // unwrap it so that \\n becomes real newlines and quotes are stripped
    const trimmed = decoded.trim();
    if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (typeof parsed === 'string') {
          decoded = parsed;
        }
      } catch {
        // not valid JSON string — keep decoded as-is
      }
    }

    return decoded;
  }

  private processAnalysisResult(resource: any, gridConfig: any): void {
    let raw = resource.content ?? resource.message ?? '';
    let message: any = this.decodeContent(raw);

    if (["chart"].indexOf(gridConfig.prompt_config_current.output_mode) > -1) {
      message = this.aiService.parseLLMJsonResponse(message);
    }
    this.outputConfig = {
      loaded: true,
      noData: false,
      askData: false,
      outputType: gridConfig.prompt_config_current.output_mode,
      summary: message
    };
    this.cdr.detectChanges();
  }

  async generateResponse() {
    if (this.promptForm.valid) {
      try {
        if (this.dialogConfig.data.aiData?.gridConfig?.prompt_config_current.ai_feature_code == AIService.ASK_YOUR_DATA || this.dialogConfig.data.aiData?.gridConfig?.prompt_config_current["ai_agent"][0]["id"]== AIService.ASK_YOUR_DATA){
          console.log(this.dialogConfig.data.aiData?.gridConfig?.prompt_config_current)
          this.getSummary()
          return;
        }
        this.isLoading = true;
        this.outputConfig = {
          loaded: false,
          noData: false,
          summary: null,
          askData:true,
        };
        this.cdr.detectChanges();

        const promptValue = this.promptForm.get('prompt').value;
        const encodedPrompt = this.aiService.encodeToLatin1(promptValue);
        const filter = "download_id:"+this.dialogConfig.data.menuData.id+",url:"+btoa(this.dialogConfig.data.menuData.url);
        
        this.aiService.getPromptResponse("DOWNLOAD_ANALYSIS", "grid_summary", encodedPrompt, filter, (promptResp) => {
          if (promptResp) {
            if(promptResp?.charts != undefined && Array.isArray(promptResp.charts) && promptResp.charts.length>0){
                for (let index = 0; index < promptResp.charts.length; index++) {
                  const element = promptResp.charts[index];
                  if (element.data == undefined){
                    element.data = {};
                    if(element.datasets != undefined){
                      element.data.datasets=element.datasets
                    }
                    if(element.labels != undefined){
                      element.data.labels=element.labels
                    }
                  }
                }
            }
            this.outputConfig = {
              loaded: true,
              noData: false,
              askData:true,
              outputType: "chart",
              summary: promptResp
            };
          } else {
            this.outputConfig = {
              loaded: true,
              noData: true,
              askData:true,
              summary: null
            };
          }
          this.isLoading = false;
          this.cdr.detectChanges();
        });
      } catch (error) {
        this.alertService.error('Failed to generate response');
        this.outputConfig = {
          loaded: true,
          noData: true,
          askData:true,
          summary: null
        };
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    }
  }
}